/*
 * Interactions for the redesigned marketing site.
 *
 * Everything is opt-in via data attributes.
 */

import {
    hasStartedDemo,
    initLeadForms,
    persistPartnerFromUrl,
    resetLeadForm,
} from "./site/lead.js";
import { initDemoProgress } from "./site/demo-progress.js";
import { initMegaMenus } from "./site/nav-mega.js";
import { initLocaleSwitch } from "./site/locale.js";

/* ------------------------------------------------------------------ */
/* Navigation                                                          */
/* ------------------------------------------------------------------ */

function initNav() {
    const nav = document.querySelector("[data-site-nav]");
    if (!nav) return;

    const panel = nav.querySelector("[data-site-nav-panel]");
    const toggle = nav.querySelector("[data-site-nav-toggle]");

    toggle?.addEventListener("click", () => {
        // classList.toggle reports whether the class is now present, so a
        // returned true means the panel just became hidden.
        const isHidden = panel.classList.toggle("hidden");
        toggle.setAttribute("aria-expanded", String(!isHidden));
        document.body.style.overflow = isHidden ? "" : "hidden";
    });

    panel?.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", () => {
            panel.classList.add("hidden");
            toggle?.setAttribute("aria-expanded", "false");
            document.body.style.overflow = "";
        });
    });

    // Elevate the bar once the hero starts scrolling away.
    const onScroll = () => {
        nav.querySelector("[data-site-nav-bar]")?.classList.toggle("shadow-lift", window.scrollY > 12);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
}

/* ------------------------------------------------------------------ */
/* Scroll reveal                                                       */
/* ------------------------------------------------------------------ */

function initReveal() {
    const targets = document.querySelectorAll(".js-reveal");
    if (!targets.length) return;

    // Content is visible until we hide it here, so bail out before hiding
    // anything if we cannot animate it back in.
    if (
        !("IntersectionObserver" in window) ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                entry.target.classList.remove("is-pending");
                observer.unobserve(entry.target);
            });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );

    // Everything already on screen when the bundle runs stays as the browser
    // painted it: hiding it here and fading it back in reads as a flash on
    // every page load. Only what is still below the fold gets animated.
    const foldBottom = window.innerHeight;

    const below = [...targets].filter(
        (el) => el.getBoundingClientRect().top > foldBottom
    );

    below.forEach((el) => {
        el.classList.add("is-pending");
        observer.observe(el);
    });
}

/* ------------------------------------------------------------------ */
/* Modals                                                              */
/* ------------------------------------------------------------------ */

const modalReturnFocus = new WeakMap();

function focusableIn(modal) {
    return [...modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    )].filter((el) => el.tabIndex >= 0 && !el.closest("[hidden]") && !el.closest('[aria-hidden="true"]'));
}

function setBackgroundInert(modal, on) {
    [...document.body.children].forEach((node) => {
        if (node === modal || node.contains(modal) || modal.contains(node)) return;
        if (node.tagName === "SCRIPT") return;

        if (on) {
            if (!node.hasAttribute("inert")) {
                node.setAttribute("data-modal-inert", "");
                node.setAttribute("inert", "");
            }
            return;
        }

        if (node.hasAttribute("data-modal-inert")) {
            node.removeAttribute("inert");
            node.removeAttribute("data-modal-inert");
        }
    });
}

function openModal(modal, trigger) {
    window.__siteLoadTurnstile?.();
    modalReturnFocus.set(modal, trigger || document.activeElement);
    modal.classList.remove("hidden");
    document.body.classList.add("is-modal-open");
    document.body.style.overflow = "hidden";
    setBackgroundInert(modal, true);
    modal.querySelectorAll("[data-lead-form]").forEach(resetLeadForm);
    const field = focusableIn(modal).find((el) => el.matches("input, select, textarea"));
    (field || focusableIn(modal)[0])?.focus();
}

function closeModal(modal) {
    const back = modalReturnFocus.get(modal);
    modal.classList.add("hidden");
    document.body.classList.remove("is-modal-open");
    document.body.style.overflow = "";
    setBackgroundInert(modal, false);
    modalReturnFocus.delete(modal);
    if (back && typeof back.focus === "function") back.focus();
}

function initModals() {
    document.querySelectorAll("[data-modal-open]").forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
            const modal = document.getElementById(
                trigger.getAttribute("data-modal-open")
            );
            if (!modal) return;
            event.preventDefault();
            openModal(modal, trigger);
        });
    });

    document.querySelectorAll("[data-modal]").forEach((modal) => {
        modal.querySelectorAll("[data-modal-close]").forEach((btn) => {
            btn.addEventListener("click", () => closeModal(modal));
        });

        // Backdrop click, but not clicks bubbling out of the dialog itself.
        modal.addEventListener("click", (event) => {
            if (event.target === modal) closeModal(modal);
        });
    });

    document.addEventListener("keydown", (event) => {
        const open = document.querySelector("[data-modal]:not(.hidden)");

        if (event.key === "Tab" && open) {
            const items = focusableIn(open);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
            return;
        }

        if (event.key !== "Escape") return;

        // Открытый список кодов страны — тоже слой: сначала закрываем его,
        // иначе Escape неожиданно закрывает всю форму.
        const openMenus = document.querySelectorAll("[data-dial-menu]:not(.hidden)");
        if (openMenus.length) {
            openMenus.forEach((menu) => menu.classList.add("hidden"));
            document.querySelectorAll("[data-dial-toggle]").forEach((toggle) => {
                toggle.setAttribute("aria-expanded", "false");
            });
            return;
        }

        document
            .querySelectorAll("[data-modal]:not(.hidden)")
            .forEach((modal) => closeModal(modal));
    });
}

const DEMO_MODAL_ID = "lead-modal";
const DEMO_MODAL_SEEN_KEY = "shamcrm.demo_modal_seen";
const DEMO_MODAL_DELAY_MS = 30000;

function hasSeenDemoModal() {
    try {
        return localStorage.getItem(DEMO_MODAL_SEEN_KEY) === "1";
    } catch (error) {
        return false;
    }
}

function markDemoModalSeen() {
    try {
        localStorage.setItem(DEMO_MODAL_SEEN_KEY, "1");
    } catch (error) {
        // Private mode can block storage; the popup may appear again this visit.
    }
}

/**
 * Автопоказ окна демо: один раз на посетителя и только на страницах, которые
 * его запросили через :demo-modal-auto. Кто уже начал создавать демо, окно
 * больше не видит.
 */
function initAutoDemoModal() {
    if (!document.querySelector("[data-demo-modal-auto]")) return;
    if (hasSeenDemoModal() || hasStartedDemo()) return;

    const modal = document.getElementById(DEMO_MODAL_ID);
    if (!modal) return;

    const open = () => {
        if (modal.classList.contains("hidden")) openModal(modal);
        cancel();
    };

    const timer = window.setTimeout(open, DEMO_MODAL_DELAY_MS);

    // Курсор ушёл за верхнюю кромку окна — посетитель собирается закрыть
    // вкладку, и предложение уже не перебивает чтение.
    const onExitIntent = (event) => {
        if (event.clientY <= 0) open();
    };

    function cancel() {
        window.clearTimeout(timer);
        document.removeEventListener("mouseout", onExitIntent);
        markDemoModalSeen();
    }

    if (window.matchMedia("(hover: hover)").matches) {
        document.addEventListener("mouseout", onExitIntent);
    }

    document.querySelectorAll(`[data-modal-open="${DEMO_MODAL_ID}"]`).forEach((trigger) => {
        trigger.addEventListener("click", cancel);
    });

    modal.querySelectorAll("[data-modal-close]").forEach((btn) => {
        btn.addEventListener("click", markDemoModalSeen);
    });

    modal.addEventListener("click", (event) => {
        if (event.target === modal) markDemoModalSeen();
    });
}

/* ------------------------------------------------------------------ */
/* YouTube previews and testimonial / card rails                       */
/* ------------------------------------------------------------------ */

function initYoutubePreviews() {
    document.querySelectorAll("[data-youtube-preview]").forEach((preview) => {
        preview.addEventListener("click", () => {
            const id = preview.dataset.youtubeId || "";
            // A YouTube id is exactly 11 url-safe characters. Anything else
            // must not be written into the iframe address.
            if (!/^[A-Za-z0-9_-]{11}$/.test(id) || preview.dataset.youtubeLoaded === "true") return;

            const iframe = document.createElement("iframe");
            iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
            iframe.title = preview.dataset.youtubeTitle || "YouTube";
            iframe.referrerPolicy = "no-referrer";
            iframe.loading = "lazy";
            iframe.allow =
                "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
            iframe.allowFullscreen = true;
            iframe.className = `${preview.className} border-0`;

            preview.dataset.youtubeLoaded = "true";
            preview.replaceWith(iframe);
            iframe.focus();
        });
    });
}

function initVideoPreviews() {
    document.querySelectorAll("[data-video-preview]").forEach((preview) => {
        preview.addEventListener("click", () => {
            const src = preview.dataset.videoSrc;
            if (!src || preview.dataset.videoLoaded === "true") return;

            const video = document.createElement("video");
            video.src = src;
            video.controls = true;
            video.autoplay = true;
            video.playsInline = true;
            video.preload = "metadata";
            video.title = preview.dataset.videoTitle || "";
            video.className = `${preview.className} border-0 bg-ink-950`;

            preview.dataset.videoLoaded = "true";
            preview.replaceWith(video);
        });
    });
}

/* Silent looping clips that stand in for animated GIFs. Poster and src stay
   in data-* attributes so the first paint does not download every clip on the
   page. Reduced motion only ever shows the poster. */
function initMotionVideos() {
    const videos = [...document.querySelectorAll("video[data-motion]")];
    if (!videos.length) return;

    const applyPoster = (video) => {
        if (video.dataset.poster && !video.getAttribute("poster")) {
            video.poster = video.dataset.poster;
        }
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        videos.forEach(applyPoster);

        return;
    }

    const start = (video) => {
        applyPoster(video);

        if (video.dataset.motionLoaded !== "true") {
            video.dataset.motionLoaded = "true";
            video.querySelectorAll("source[data-src]").forEach((source) => {
                source.src = source.dataset.src;
            });
            video.load();
        }

        // Autoplay can still be refused (data saver, low power mode); the
        // poster stays on screen in that case, which is a fine fallback.
        video.play().catch(() => {});
    };

    if (!("IntersectionObserver" in window)) {
        videos.forEach(start);

        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) start(entry.target);
                else if (entry.target.dataset.motionLoaded === "true") entry.target.pause();
            });
        },
        { rootMargin: "200px 0px" }
    );

    videos.forEach((video) => observer.observe(video));
}

function initShotCarousels() {
    document.querySelectorAll("[data-hero-carousel], [data-shot-carousel]").forEach((root) => {
        const track = root.querySelector("[data-hero-track], [data-shot-track]");
        const slides = [...root.querySelectorAll("[data-hero-slide], [data-shot-slide]")];
        const dots = [...root.querySelectorAll("[data-hero-dot], [data-shot-dot]")];
        const caption = root.querySelector("[data-hero-caption], [data-shot-caption]");

        if (!track || slides.length < 2) return;

        let index = 0;
        let timer = null;
        let touchX = 0;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let userPaused = reduced;
        const interval = Number(root.dataset.heroInterval || root.dataset.shotInterval || 3000);
        // A 3s autoplay paints a later slide as a new LCP. Wait 5s so the
        // first shot can count, then rotate as before.
        const firstWait = 5000;

        const hydrateSlide = (slide) => {
            if (!slide) return;
            slide.querySelectorAll("source[data-srcset]").forEach((source) => {
                source.srcset = source.getAttribute("data-srcset");
                source.removeAttribute("data-srcset");
            });
            slide.querySelectorAll("img[data-src]").forEach((img) => {
                const srcset = img.getAttribute("data-srcset");
                img.src = img.getAttribute("data-src");
                if (srcset) img.setAttribute("srcset", srcset);
                img.removeAttribute("data-src");
                img.removeAttribute("data-srcset");
            });
        };

        const go = (next) => {
            index = (next + slides.length) % slides.length;
            hydrateSlide(slides[index]);

            slides.forEach((slide, i) => {
                const on = i === index;
                slide.classList.toggle("is-active", on);
                slide.setAttribute("aria-hidden", on ? "false" : "true");
            });

            dots.forEach((dot, i) => {
                if (i === index) dot.setAttribute("aria-current", "true");
                else dot.removeAttribute("aria-current");
            });

            if (caption) {
                caption.textContent = dots[index]?.dataset.heroLabel || dots[index]?.dataset.shotLabel || "";
            }
        };

        const stop = () => {
            if (timer) {
                window.clearTimeout(timer);
                window.clearInterval(timer);
                timer = null;
            }
        };

        const play = (fromUser) => {
            stop();
            if (userPaused) return;
            if (!fromUser && reduced) return;

            const wait = fromUser ? interval : firstWait;
            timer = window.setTimeout(() => {
                go(index + 1);
                timer = window.setInterval(() => go(index + 1), interval);
            }, wait);
        };

        // Stop while the keyboard is inside the carousel. Resume after it leaves.
        root.addEventListener("focusin", () => stop());
        root.addEventListener("focusout", (event) => {
            if (root.contains(event.relatedTarget)) return;
            if (!userPaused) play(true);
        });

        dots.forEach((dot, i) => {
            dot.addEventListener("click", () => {
                go(i);
                if (!userPaused) play(true);
            });
        });

        track.addEventListener("touchstart", (event) => {
            touchX = event.changedTouches[0]?.clientX || 0;
        }, { passive: true });

        track.addEventListener("touchend", (event) => {
            const dx = (event.changedTouches[0]?.clientX || 0) - touchX;
            if (Math.abs(dx) < 40) return;
            go(index + (dx < 0 ? 1 : -1));
            if (!userPaused) play(true);
        }, { passive: true });

        root.addEventListener("keydown", (event) => {
            if (event.key === "ArrowLeft") {
                event.preventDefault();
                go(index - 1);
                if (!userPaused) play(true);
            }
            if (event.key === "ArrowRight") {
                event.preventDefault();
                go(index + 1);
                if (!userPaused) play(true);
            }
        });

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) stop();
            else if (!userPaused) play();
        });

        go(0);
        play();
    });
}

function initRails() {
    document.querySelectorAll("[data-rail]").forEach((rail) => {
        const track = rail.querySelector("[data-rail-track]");
        if (!track) return;

        const step = () => Math.max(track.clientWidth * 0.8, 280);

        rail.querySelector("[data-rail-prev]")?.addEventListener("click", () => {
            track.scrollBy({ left: -step(), behavior: "smooth" });
        });

        rail.querySelector("[data-rail-next]")?.addEventListener("click", () => {
            track.scrollBy({ left: step(), behavior: "smooth" });
        });
    });
}

/* ------------------------------------------------------------------ */
/* Filterable card grids (integrations, solutions)                     */
/* ------------------------------------------------------------------ */

function initFilters() {
    document.querySelectorAll("[data-filter-group]").forEach((group) => {
        const buttons = group.querySelectorAll("[data-filter]");
        const items = group.querySelectorAll("[data-filter-item]");
        const empty = group.querySelector("[data-filter-empty]");

        const apply = (value) => {
            let shown = 0;

            items.forEach((item) => {
                const categories = (item.dataset.filterItem || "").split(" ");
                const visible = value === "all" || categories.includes(value);
                item.classList.toggle("hidden", !visible);
                if (visible) shown += 1;
            });

            empty?.classList.toggle("hidden", shown > 0);

            buttons.forEach((button) => {
                const active = button.dataset.filter === value;
                button.setAttribute("aria-pressed", String(active));
                button.classList.toggle("bg-ink-900", active);
                button.classList.toggle("text-white", active);
                button.classList.toggle("border-ink-900", active);
                button.classList.toggle("bg-white", !active);
                button.classList.toggle("text-ink-600", !active);
                button.classList.toggle("border-ink-200", !active);
            });
        };

        buttons.forEach((button) => {
            button.addEventListener("click", () => apply(button.dataset.filter));
        });
    });
}

/* ------------------------------------------------------------------ */
/* Pricing: prepayment period and currency                             */
/* ------------------------------------------------------------------ */

const CURRENCY_STORAGE_KEY = "shamcrm.pricing.currency";
const COUNTRY_STORAGE_KEY = "shamcrm.pricing.country";
const DIAL_STORAGE_KEY = "shamcrm.dial";
const GEO_ENDPOINT = "https://ipapi.co/json/";
const CURRENCY_BY_COUNTRY = { UZ: "uzs", TJ: "tjs" };
const DIAL_BY_COUNTRY = {
    UZ: "+998",
    TJ: "+992",
    KZ: "+7",
    KG: "+996",
    RU: "+7",
    US: "+1",
};

function readJson(value) {
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function metaContent(name) {
    return document.querySelector(`meta[name="${name}"]`)?.content || "";
}

function rememberCountry(country) {
    if (!country) return;

    try {
        localStorage.setItem(COUNTRY_STORAGE_KEY, country);
    } catch (error) {
        // Private mode can block storage; geo still applies for this page.
    }
}

/**
 * Country of the visitor, cached so the lookup runs once per browser rather
 * than on every page view: the free tier of the geo service is capped daily
 * and starts answering with errors once the cap is hit. The server country
 * (Cloudflare) wins when it is already on the page.
 */
let countryPromise;

async function lookupCountry() {
    const fromServer = metaContent("visitor-country");
    if (fromServer) {
        rememberCountry(fromServer);
        return fromServer;
    }

    try {
        const cached = localStorage.getItem(COUNTRY_STORAGE_KEY);
        if (cached) return cached;
    } catch (error) {
        // ignore
    }

    try {
        const response = await fetch(GEO_ENDPOINT);
        const data = await response.json();
        const country = data.country_code || null;

        rememberCountry(country);

        return country;
    } catch (error) {
        return null;
    }
}

function detectCountry() {
    if (!countryPromise) countryPromise = lookupCountry();

    return countryPromise;
}

function suggestedCurrency(country) {
    if (CURRENCY_BY_COUNTRY[country]) return CURRENCY_BY_COUNTRY[country];

    return country ? "usd" : metaContent("visitor-currency") || "uzs";
}

function suggestedDial(country) {
    return DIAL_BY_COUNTRY[country] || metaContent("visitor-dial") || "+998";
}

function initPricing() {
    const root = document.querySelector("[data-pricing]");
    if (!root) return;

    const periodButtons = root.querySelectorAll("[data-period-option]");
    const modelButtons = root.querySelectorAll("[data-model-option]");
    const modelDescription = root.querySelector("[data-model-description]");
    const currencySelect = root.querySelector("[data-currency-select]");

    let period = root.dataset.period;
    let model = root.dataset.model;
    let currency = root.dataset.currency;

    const render = () => {
        root.querySelectorAll("[data-price]").forEach((element) => {
            const prices = readJson(element.dataset.price);
            const entry = prices?.[period]?.[currency];
            if (!entry) return;

            element.textContent = entry.price;

            const was = element.closest("[data-price-block]")?.querySelector("[data-price-was]")
                || element.parentElement?.querySelector("[data-price-was]");
            if (was) {
                was.textContent = entry.was || "";
                was.classList.toggle("hidden", !entry.was);
            }
        });

        root.querySelectorAll("[data-price-currency]").forEach((element) => {
            const prices = readJson(element.dataset.priceCurrency);
            if (prices?.[currency]) element.textContent = prices[currency];
        });

        root.querySelectorAll("[data-price-model]").forEach((element) => {
            const prices = readJson(element.dataset.priceModel);
            const price = prices?.[model]?.[currency];
            if (price) element.textContent = price;
        });

        root.querySelectorAll("[data-limit-model]").forEach((element) => {
            const limits = readJson(element.dataset.limitModel);
            if (limits?.[model]) element.textContent = limits[model];
        });

        periodButtons.forEach((button) => {
            button.setAttribute("aria-pressed", String(button.dataset.periodOption === period));
        });

        modelButtons.forEach((button) => {
            button.setAttribute("aria-pressed", String(button.dataset.modelOption === model));
        });
    };

    periodButtons.forEach((button) => {
        button.addEventListener("click", () => {
            period = button.dataset.periodOption;
            render();
        });
    });

    modelButtons.forEach((button) => {
        button.addEventListener("click", () => {
            model = button.dataset.modelOption;
            if (modelDescription) modelDescription.textContent = button.dataset.modelText;
            render();
        });
    });

    const setCurrency = (value) => {
        if (!value || value === currency) return;
        currency = value;
        if (currencySelect) currencySelect.value = value;
        render();
    };

    currencySelect?.addEventListener("change", () => {
        localStorage.setItem(CURRENCY_STORAGE_KEY, currencySelect.value);
        setCurrency(currencySelect.value);
    });

    // A stored choice wins over geography; only guess for first-time visitors.
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored && currencySelect?.querySelector(`option[value="${stored}"]`)) {
        setCurrency(stored);
        return;
    }

    detectCountry().then((country) => {
        if (!country) return;

        const next = suggestedCurrency(country);
        if (currencySelect?.querySelector(`option[value="${next}"]`)) {
            setCurrency(next);
            return;
        }

        const match = [...(currencySelect?.options || [])].find(
            (option) => option.dataset.country === country
        );
        if (match) setCurrency(match.value);
    });
}

/* ------------------------------------------------------------------ */
/* AI agent calculators                                                */
/* ------------------------------------------------------------------ */

/**
 * Arithmetic on the visitor's own numbers, shown on the AI agent pages. The
 * same formulas render server-side in components/site/ai/calculator.blade.php,
 * so the block is already correct before this runs — keep the two in step.
 *
 * Assumptions, spelled out in the copy next to the inputs: 22 working days in
 * a month and an 8-hour day.
 */
const CALCULATORS = {
    chat: ({ leads, share }) => {
        const month = Math.round((leads * share) / 100);

        return { month, year: month * 12 };
    },
    calls: ({ managers, calls, minutes }) => {
        const total = managers * calls * 22;
        const hours = Math.round((total * minutes) / 60);

        return { calls: total, hours, days: Math.round(hours / 8) };
    },
};

function groupDigits(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
}

function initCalculators() {
    document.querySelectorAll("[data-calc]").forEach((root) => {
        const compute = CALCULATORS[root.dataset.calc];
        if (!compute) return;

        const inputs = root.querySelectorAll("[data-calc-input]");

        const render = () => {
            const values = {};

            inputs.forEach((input) => {
                // Clamp to the declared bounds so a stray keystroke cannot turn
                // the block into a nonsense number.
                const min = Number(input.min || 0);
                const max = Number(input.max || Infinity);
                const value = Number(input.value);

                values[input.dataset.calcInput] = Number.isFinite(value)
                    ? Math.min(Math.max(value, min), max)
                    : min;
            });

            const results = compute(values);

            root.querySelectorAll("[data-calc-output]").forEach((output) => {
                const result = results[output.dataset.calcOutput];
                if (result !== undefined) output.textContent = groupDigits(result);
            });
        };

        inputs.forEach((input) => {
            input.addEventListener("input", render);
            input.addEventListener("change", render);
        });
    });
}

/* ------------------------------------------------------------------ */
/* Dial code pickers                                                   */
/* ------------------------------------------------------------------ */

function initDialCodePickers() {
    document.querySelectorAll("[data-dial-picker]").forEach((picker) => {
        const hidden = picker.querySelector("[data-lead-dial-code]");
        if (!hidden) return;

        // Код страны задаёт ожидаемую длину номера, поэтому форма должна
        // узнать о его смене и пересчитать состояние кнопки.
        const commit = (value, persist = true) => {
            hidden.value = value;
            hidden.dispatchEvent(new Event("change", { bubbles: true }));
            if (persist) {
                try {
                    localStorage.setItem(DIAL_STORAGE_KEY, value);
                } catch (error) {
                    // ignore
                }
            }
        };

        const nativeSelect = picker.querySelector("[data-dial-select]");
        if (nativeSelect) {
            nativeSelect.addEventListener("change", () => commit(nativeSelect.value));
            hidden.value = nativeSelect.value;
            picker.__applyDial = (dial) => {
                if (!dial || !nativeSelect.querySelector(`option[value="${dial}"]`)) return;
                nativeSelect.value = dial;
                commit(dial, false);
            };
            return;
        }

        const toggle = picker.querySelector("[data-dial-toggle]");
        const menu = picker.querySelector("[data-dial-menu]");
        const codeEl = picker.querySelector("[data-dial-code]");
        const flagEl = picker.querySelector("[data-dial-flag]");
        if (!toggle || !menu) return;

        const paint = (option) => {
            const code = option?.dataset.code || "+998";
            commit(code, false);
            if (codeEl) codeEl.textContent = code;
            if (flagEl && option?.dataset.flag) {
                flagEl.src = option.dataset.flag;
                flagEl.alt = option.dataset.label || "";
            }
        };

        const setOpen = (open) => {
            menu.classList.toggle("hidden", !open);
            toggle.setAttribute("aria-expanded", open ? "true" : "false");
        };

        toggle.addEventListener("click", (event) => {
            event.preventDefault();
            const willOpen = menu.classList.contains("hidden");
            document.querySelectorAll("[data-dial-menu]").forEach((other) => {
                other.classList.add("hidden");
            });
            document.querySelectorAll("[data-dial-toggle]").forEach((other) => {
                other.setAttribute("aria-expanded", "false");
            });
            setOpen(willOpen);
        });

        menu.querySelectorAll("[data-dial-option]").forEach((option) => {
            option.addEventListener("click", () => {
                paint(option);
                try {
                    localStorage.setItem(DIAL_STORAGE_KEY, option.dataset.code || "+998");
                } catch (error) {
                    // ignore
                }
                setOpen(false);
            });
        });

        picker.__applyDial = (dial, country) => {
            const options = [...menu.querySelectorAll("[data-dial-option]")];
            const match = options.find((option) => country && option.dataset.country === country)
                || options.find((option) => option.dataset.code === dial);
            if (match) paint(match);
        };
    });

    document.addEventListener("click", (event) => {
        if (event.target.closest("[data-dial-picker]")) return;

        document.querySelectorAll("[data-dial-menu]").forEach((menu) => {
            menu.classList.add("hidden");
        });
        document.querySelectorAll("[data-dial-toggle]").forEach((toggle) => {
            toggle.setAttribute("aria-expanded", "false");
        });
    });
}

const COOKIE_KEY = "shamcrm.cookies";

function cookieChoice() {
    try {
        return localStorage.getItem(COOKIE_KEY);
    } catch (error) {
        return null;
    }
}

function setCookieChoice(value) {
    try {
        localStorage.setItem(COOKIE_KEY, value);
    } catch (error) {
        // Private mode can block storage; the banner just stays gone this visit.
    }
}

function initCookies() {
    const banner = document.querySelector("[data-cookie-banner]");
    const choice = cookieChoice();

    // Analytics does not wait for this bar. Hiding it only restores the
    // sticky CTA that is-cookie-open covers.
    if (choice === "all" || choice === "necessary") {
        return;
    }

    if (!banner) return;

    banner.hidden = false;
    document.body.classList.add("is-cookie-open");

    // Banner copy: using the site is consent. Ok is one way to say that.
    // Scroll or a click outside the bar is the same signal, so the bar
    // does not sit over the page for people who never press Ok.
    const acceptAll = () => {
        setCookieChoice("all");
        banner.hidden = true;
        document.body.classList.remove("is-cookie-open");
        window.removeEventListener("scroll", onContinue);
        document.removeEventListener("pointerdown", onContinue);
    };

    const onContinue = (event) => {
        if (event.type !== "scroll" && event.target.closest("[data-cookie-banner]")) {
            return;
        }

        acceptAll();
    };

    banner.querySelector("[data-cookie-accept]")?.addEventListener("click", acceptAll);
    window.addEventListener("scroll", onContinue, { passive: true });
    document.addEventListener("pointerdown", onContinue, { passive: true });
}

function initProgramPickers() {
    const select = document.querySelector("[data-lead-form] [name=program]");
    if (!select) return;

    const apply = (track) => {
        const option = Array.from(select.options).find(
            (item) => item.dataset.track === track
        );
        if (option) select.value = option.value;
    };

    const fromHash = () => {
        const hash = window.location.hash.replace("#", "");
        if (hash === "referral-program") apply("referral");
        if (hash === "partner-program") apply("partner");
    };

    fromHash();
    window.addEventListener("hashchange", fromHash);

    document.querySelectorAll("[data-set-program]").forEach((link) => {
        link.addEventListener("click", () => apply(link.dataset.setProgram || ""));
    });
}

function boot() {
    persistPartnerFromUrl();
    initNav();
    initMegaMenus();
    initLocaleSwitch();
    initReveal();
    initModals();
    initAutoDemoModal();
    initLeadForms();
    initDemoProgress();
    initProgramPickers();
    initYoutubePreviews();
    initVideoPreviews();
    initMotionVideos();
    initShotCarousels();
    initRails();
    initFilters();
    initPricing();
    initCalculators();
    initDialCodePickers();
    initCookies();

    const storedDial = (() => {
        try {
            return localStorage.getItem(DIAL_STORAGE_KEY);
        } catch (error) {
            return null;
        }
    })();

    if (storedDial) {
        document.querySelectorAll("[data-dial-picker]").forEach((picker) => {
            picker.__applyDial?.(storedDial);
        });
    }

    detectCountry().then((country) => {
        if (storedDial || !country) return;

        const dial = suggestedDial(country);
        document.querySelectorAll("[data-dial-picker]").forEach((picker) => {
            picker.__applyDial?.(dial, country);
        });
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
} else {
    boot();
}
